// src/services/UserService.js
import ApiService from '../base/BaseApiService';

/**
 * 新規ユーザーを作成する
 * @param {Object} userData - ユーザーデータ
 * @param {string} userData.username - ユーザー名
 * @param {string} userData.accountname - アカウント名（メールアドレス）
 * @param {boolean} userData.is_admin - 管理者権限の有無
 * @param {boolean} userData.is_agent - 代理店権限の有無
 * @param {boolean} userData.is_solicitor - 募集人権限の有無
 * @returns {Promise<Object>} - 作成されたユーザー情報
 */
export const createUser = async (userData) => {
  try {
    return await ApiService.post('/users/', userData);
  } catch (error) {
    console.error('Error creating user:', {
      errorId: error.errorId,
      userData: { ...userData, password: '[HIDDEN]' } // パスワードは非表示
    });
    throw error;
  }
};

/**
 * ユーザー一覧を取得する（管理者向け）
 * @param {Object} options - 取得オプション
 * @param {number} [options.skip=0] - スキップ数
 * @param {number} [options.limit=100] - 取得上限
 * @param {boolean} [options.is_admin] - 管理者フィルター
 * @param {boolean} [options.is_agent] - 代理店フィルター
 * @param {boolean} [options.is_solicitor] - 募集人フィルター
 * @param {boolean} [options.is_active] - アクティブフィルター
 * @returns {Promise<Array>} - ユーザー一覧
 */
export const listUsers = async (options = {}) => {
  const { skip = 0, limit = 100, is_admin, is_agent, is_solicitor, is_active } = options;
  
  const params = { 
    skip, 
    limit, 
    is_admin: is_admin !== undefined ? is_admin : undefined,
    is_agent: is_agent !== undefined ? is_agent : undefined, 
    is_solicitor: is_solicitor !== undefined ? is_solicitor : undefined,
    is_active: is_active !== undefined ? is_active : undefined
  };

  try {
    return await ApiService.get('/users/', params);
  } catch (error) {
    console.error('Error listing users:', {
      errorId: error.errorId,
      params,
      statusCode: error.response?.status
    });
    throw error;
  }
};

/**
 * 現在のログインユーザー情報を取得する
 * @returns {Promise<Object>} - ユーザー情報
 */
export const getCurrentUser = async () => {
  try {
    return await ApiService.get('/users/me');
  } catch (error) {
    console.error('Error fetching current user:', {
      errorId: error.errorId,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
};

/**
 * 指定IDのユーザー情報を取得する（管理者向け）
 * @param {number} userId - ユーザーID
 * @returns {Promise<Object>} - ユーザー情報
 */
export const getUserById = async (userId) => {
  try {
    return await ApiService.get(`/users/${userId}`);
  } catch (error) {
    console.error(`Error fetching user ${userId}:`, {
      errorId: error.errorId,
      userId,
      statusCode: error.response?.status
    });
    throw error;
  }
};

/**
 * 現在のユーザー情報を更新する
 * @param {Object} updateData - 更新データ
 * @param {string} [updateData.username] - 新しいユーザー名
 * @param {string} [updateData.accountname] - 新しいアカウント名（メールアドレス）
 * @param {string} [updateData.password] - 新しいパスワード
 * @returns {Promise<Object>} - 更新されたユーザー情報
 */
export const updateCurrentUser = async (updateData) => {
  try {
    return await ApiService.put('/users/me', updateData);
  } catch (error) {
    console.error('Error updating current user:', {
      errorId: error.errorId,
      updateFields: Object.keys(updateData),
      statusCode: error.response?.status
    });
    throw error;
  }
};

/**
 * 管理者が特定ユーザーの情報を更新する
 * @param {number} userId - ユーザーID
 * @param {Object} updateData - 更新データ
 * @param {string} [updateData.username] - 新しいユーザー名
 * @param {string} [updateData.accountname] - 新しいアカウント名（メールアドレス）
 * @param {string} [updateData.password] - 新しいパスワード
 * @param {boolean} [updateData.is_admin] - 管理者権限の有無
 * @param {boolean} [updateData.is_agent] - 代理店権限の有無
 * @param {boolean} [updateData.is_solicitor] - 募集人権限の有無
 * @param {boolean} [updateData.is_active] - アクティブ状態の有無
 * @returns {Promise<Object>} - 更新されたユーザー情報
 */
export const updateUser = async (userId, updateData) => {
  try {
    return await ApiService.put(`/users/${userId}`, updateData);
  } catch (error) {
    console.error(`Error updating user ${userId}:`, {
      errorId: error.errorId,
      userId,
      updateFields: Object.keys(updateData),
      statusCode: error.response?.status
    });
    throw error;
  }
};

/**
 * パスワードリセットのリクエストを送信する
 * @param {string} accountname - アカウント名（メールアドレス）
 * @returns {Promise<Object>} - 処理結果
 */
export const requestPasswordReset = async (accountname) => {
  try {
    return await ApiService.post('/users/reset-password-request', { 
      accountname 
    });
  } catch (error) {
    console.error('Error requesting password reset:', {
      errorId: error.errorId,
      accountname: accountname.replace(/(.{2}).*(@.*)/, '$1***$2'), // 部分マスク
      statusCode: error.response?.status
    });
    throw error;
  }
};

/**
 * パスワードリセットを実行する
 * @param {string} token - リセットトークン
 * @param {string} newPassword - 新しいパスワード
 * @returns {Promise<Object>} - 処理結果
 */
export const resetPassword = async (token, newPassword) => {
  try {
    return await ApiService.post('/users/reset-password', {
      token,
      new_password: newPassword
    });
  } catch (error) {
    console.error('Error resetting password:', {
      errorId: error.errorId,
      hasToken: !!token,
      statusCode: error.response?.status
    });
    throw error;
  }
};

export default {
  createUser,
  listUsers,
  getCurrentUser,
  getUserById,
  updateCurrentUser,
  updateUser,
  requestPasswordReset,
  resetPassword
};